import React, { useState } from 'react';
import { useToast } from '../../../components/ui/Toast';
import { useTranslation } from 'react-i18next';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';

const RolesForm = ({ roles, onRolesChange, errors }) => {
  // Replaces alert(): non-blocking, styled, translatable, and errors
  // persist until dismissed instead of vanishing.
  const { success: toastSuccess, error: toastError } = useToast();
  const { t } = useTranslation('conferences');
  const k = (key, opts) => t(`setupWizard.roles.${key}`, opts);

  const [newRole, setNewRole] = useState({
    email: '',
    name: '',
    role: 'reviewer',
    permissions: []
  });

  const roleOptions = ['chair', 'organizer', 'reviewer', 'author']?.map(value => ({
    value,
    label: k(`roleOptions.${value}`)
  }));

  // Permission ids are stable; only the labels are translated.
  const permissionsByRole = {
    chair: [
      { id: 'manage_conference', labelKey: 'manage_conference', default: true },
      { id: 'assign_reviewers', labelKey: 'assign_reviewers', default: true },
      { id: 'make_decisions', labelKey: 'make_decisions', default: true },
      { id: 'send_notifications', labelKey: 'send_notifications', default: true },
      { id: 'view_all_submissions', labelKey: 'view_all_submissions', default: true },
      { id: 'manage_users', labelKey: 'manage_users', default: true }
    ],
    organizer: [
      { id: 'manage_tracks', labelKey: 'manage_tracks', default: true },
      { id: 'view_submissions', labelKey: 'view_submissions', default: true },
      { id: 'assign_reviewers', labelKey: 'assign_reviewers_short', default: true },
      { id: 'send_notifications', labelKey: 'send_notifications_short', default: false }
    ],
    reviewer: [
      { id: 'bid_papers', labelKey: 'bid_papers', default: true },
      { id: 'review_papers', labelKey: 'review_papers', default: true },
      { id: 'view_reviews', labelKey: 'view_reviews', default: false }
    ],
    author: [
      { id: 'submit_papers', labelKey: 'submit_papers', default: true },
      { id: 'view_own_submissions', labelKey: 'view_own_submissions', default: true },
      { id: 'edit_submissions', labelKey: 'edit_submissions', default: true }
    ]
  };

  const handleAddRole = () => {
    if (!newRole?.email?.trim() || !newRole?.name?.trim()) return;

    const defaultPermissions = permissionsByRole?.[newRole?.role]?.filter(p => p?.default)?.map(p => p?.id);

    const role = {
      id: Date.now(),
      email: newRole?.email,
      name: newRole?.name,
      role: newRole?.role,
      permissions: newRole?.permissions?.length > 0 ? newRole?.permissions : defaultPermissions,
      invitedAt: new Date()?.toISOString(),
      status: 'pending'
    };

    onRolesChange([...roles, role]);
    setNewRole({ email: '', name: '', role: 'reviewer', permissions: [] });
  };

  const handleRemoveRole = (roleId) => {
    onRolesChange(roles?.filter(r => r?.id !== roleId));
  };

  const handlePermissionToggle = (permissionId) => {
    const permissions = newRole?.permissions?.includes(permissionId)
      ? newRole?.permissions?.filter(p => p !== permissionId)
      : [...newRole?.permissions, permissionId];

    setNewRole({ ...newRole, permissions });
  };

  const handleBulkImport = () => {
    toastSuccess(k('bulkImportAlert'));
  };

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="bg-card rounded-lg border border-border p-4 md:p-6">
        <div className="flex items-center justify-between mb-4 md:mb-6">
          <h3 className="text-base md:text-lg font-semibold text-foreground">{k('addTeamMember')}</h3>
          <Button
            variant="outline"
            size="sm"
            onClick={handleBulkImport}
            iconName="Upload"
            iconPosition="left"
          >
            {k('bulkImport')}
          </Button>
        </div>

        <div className="space-y-4 md:space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            <Input
              label={k('email.label')}
              type="email"
              placeholder={k('email.placeholder')}
              value={newRole?.email}
              onChange={(e) => setNewRole({ ...newRole, email: e?.target?.value })}
              required
              description={k('email.description')}
            />

            <Input
              label={k('name.label')}
              type="text"
              placeholder={k('name.placeholder')}
              value={newRole?.name}
              onChange={(e) => setNewRole({ ...newRole, name: e?.target?.value })}
              required
            />
          </div>

          <Select
            label={k('role.label')}
            options={roleOptions}
            value={newRole?.role}
            onChange={(value) => setNewRole({ ...newRole, role: value, permissions: [] })}
            required
            description={k('role.description')}
          />

          <div>
            <label className="block text-sm font-medium text-foreground mb-3">
              {k('permissionsLabel')}
            </label>
            <div className="bg-muted/50 rounded-lg p-4 space-y-3">
              {permissionsByRole?.[newRole?.role]?.map((permission) => (
                <Checkbox
                  key={permission?.id}
                  label={k(`permissions.${permission?.labelKey}`)}
                  checked={
                    newRole?.permissions?.length === 0
                      ? permission?.default
                      : newRole?.permissions?.includes(permission?.id)
                  }
                  onChange={() => handlePermissionToggle(permission?.id)}
                />
              ))}
            </div>
          </div>

          <Button
            variant="default"
            onClick={handleAddRole}
            iconName="UserPlus"
            iconPosition="left"
            disabled={!newRole?.email?.trim() || !newRole?.name?.trim()}
          >
            {k('sendInvitation')}
          </Button>
        </div>
      </div>
      {roles?.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-base md:text-lg font-semibold text-foreground">
            {k('teamMembers', { count: roles?.length })}
          </h3>

          <div className="grid grid-cols-1 gap-3 md:gap-4">
            {roles?.map((role) => (
              <div
                key={role?.id}
                className="bg-card rounded-lg border border-border p-4 md:p-6"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 md:gap-4 flex-1 min-w-0">
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon
                        name={
                          role?.role === 'chair' ? 'Crown'
                            : role?.role === 'organizer' ? 'Users'
                            : role?.role === 'reviewer' ? 'ClipboardCheck' : 'FileText'
                        }
                        size={20}
                        className="text-primary"
                      />
                    </div>

                    <div className="flex-1 min-w-0">
                      <h4 className="text-base font-semibold text-foreground mb-1">
                        {role?.name}
                      </h4>
                      <p className="text-sm text-muted-foreground mb-2">{role?.email}</p>

                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        <span className="px-2 py-1 bg-primary/10 text-primary text-xs font-medium caption">
                          {roleOptions?.find(r => r?.value === role?.role)?.label}
                        </span>
                        <span
                          className={`px-2 py-1 text-xs font-medium caption ${
                            role?.status === 'pending' ? 'bg-warning/10 text-warning' : 'bg-success/10 text-success'
                          }`}
                        >
                          {role?.status === 'pending' ? k('invitationPending') : k('active')}
                        </span>
                      </div>

                      <div className="flex flex-wrap gap-1.5">
                        {role?.permissions?.slice(0, 3)?.map((permId) => {
                          const perm = permissionsByRole?.[role?.role]?.find(p => p?.id === permId);
                          return perm ? (
                            <span
                              key={permId}
                              className="px-2 py-1 bg-muted text-muted-foreground text-xs"
                            >
                              {k(`permissions.${perm?.labelKey}`)}
                            </span>
                          ) : null;
                        })}
                        {role?.permissions?.length > 3 && (
                          <span className="px-2 py-1 bg-muted text-muted-foreground text-xs">
                            {k('morePermissions', { count: role?.permissions?.length - 3 })}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveRole(role?.id)}
                    iconName="Trash2"
                    className="text-error hover:text-error flex-shrink-0"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {errors?.roles && (
        <p className="text-sm text-error">{errors?.roles}</p>
      )}
    </div>
  );
};

export default RolesForm;
